import React, { useState, useEffect } from 'react';
import api from '../utils/axios';
import { message, Card, Button, Drawer } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { useLocation, useSearchParams } from 'react-router-dom';

// 导入组件
import EmployeeStats from '../components/EmployeeStats';
import EmployeeSearchForm from '../components/EmployeeSearchForm';
import EmployeeList from '../components/EmployeeList';

import EmployeeDetail from '../components/EmployeeDetail';

const Employees = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [urlSearchParams] = useSearchParams();
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(false);

  const [detailVisible, setDetailVisible] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [viewingEmployee, setViewingEmployee] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [searchParams, setSearchParams] = useState({});
  const [stats, setStats] = useState({
    total: 0,
    statusStats: [],
    deptStats: []
  });

  // 获取员工列表
  const fetchEmployees = async (extraParams = {}) => {
    setLoading(true);
    try {
      // 构建基础参数
      const baseParams = {
        page: extraParams.page || pagination.current,
        limit: extraParams.limit || pagination.pageSize,
        ...searchParams,
        ...extraParams
      };
      
      // 构建最终参数，确保默认只显示在职员工
      // 只有当明确设置了 employment_status 时才使用该值，否则默认显示在职员工
      const params = {
        employment_status: 'employed', // 默认只显示在职员工
        ...baseParams
      };
      
      // 如果明确设置了 employment_status 为 undefined 或空字符串，则移除该参数
      if (baseParams.hasOwnProperty('employment_status') && (baseParams.employment_status === undefined || baseParams.employment_status === '')) {
        delete params.employment_status;
      }
      
      // 如果明确设置了 department_id 为 undefined 或空字符串，则移除该参数
      if (baseParams.hasOwnProperty('department_id') && (baseParams.department_id === undefined || baseParams.department_id === '')) {
        delete params.department_id;
      }
      
      // 如果明确设置了 position_id 为 undefined 或空字符串，则移除该参数
      if (baseParams.hasOwnProperty('position_id') && (baseParams.position_id === undefined || baseParams.position_id === '')) {
        delete params.position_id;
      }
      
      // 如果明确设置了 progress 为 undefined 或空字符串，则移除该参数
      if (baseParams.hasOwnProperty('progress') && (baseParams.progress === undefined || baseParams.progress === '')) {
        delete params.progress;
      }
      
      // 如果明确设置了 start_date 为 undefined 或空字符串，则移除该参数
      if (baseParams.hasOwnProperty('start_date') && (baseParams.start_date === undefined || baseParams.start_date === '')) {
        delete params.start_date;
      }
      
      // 如果明确设置了 end_date 为 undefined 或空字符串，则移除该参数
      if (baseParams.hasOwnProperty('end_date') && (baseParams.end_date === undefined || baseParams.end_date === '')) {
        delete params.end_date;
      }
      
      // 如果明确设置了 search 为 undefined 或空字符串，则移除该参数
      if (baseParams.hasOwnProperty('search') && (baseParams.search === undefined || baseParams.search === '')) {
        delete params.search;
      }
      console.log('📋 获取员工列表，参数:', params);
      const response = await api.get('/employees', { params });
      console.log('📡 员工列表返回:', { 
        success: response.data.success, 
        employeeCount: response.data.data?.employees?.length,
        total: response.data.data?.pagination?.total
      });
      
      if (response.data.success) {
        const resData = response.data.data || {};
        setEmployees(resData.employees || []);
        console.log('✅ 员工列表已更新，共', resData.employees?.length, '条记录');
        if (resData.pagination) {
          setPagination(prev => ({
            ...prev,
            current: resData.pagination.page || prev.current,
            pageSize: resData.pagination.limit || prev.pageSize,
            total: resData.pagination.total || prev.total
          }));
        }
      }
    } catch (error) {
      console.error('❌ 获取员工列表失败:', error);
      message.error('获取员工列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取部门列表
  const fetchDepartments = async () => {
    try {
      const response = await api.get('/departments');
      if (response.data.success) {
        setDepartments(response.data.data || []);
      }
    } catch (error) {
      console.error('获取部门列表失败:', error);
    }
  };

  // 获取职位列表
  const fetchPositions = async () => {
    try {
      const response = await api.get('/positions');
      if (response.data.success) {
        setPositions(response.data.data || []);
      }
    } catch (error) {
      console.error('获取职位列表失败:', error);
    }
  };

  // 获取统计数据
  const fetchStats = async () => {
    try {
      const response = await api.get('/employees/stats/overview');
      if (response.data.success) {
        setStats(response.data.data || {});
      }
    } catch (error) {
      console.error('获取统计数据失败:', error);
    }
  };

  // 处理URL参数自动搜索
  useEffect(() => {
    const urlSearch = urlSearchParams.get('search');
    const urlEmployeeName = urlSearchParams.get('employee_name');
    const urlEmployeeNo = urlSearchParams.get('employee_no');
    
    if (urlSearch || urlEmployeeName || urlEmployeeNo) {
      // 构建搜索参数
      const autoSearchParams = {};
      if (urlSearch) {
        autoSearchParams.search = urlSearch;
      } else if (urlEmployeeName) {
        autoSearchParams.search = urlEmployeeName;
      } else if (urlEmployeeNo) {
        autoSearchParams.search = urlEmployeeNo;
      }
      
      console.log('🔍 自动搜索员工:', autoSearchParams);
      setSearchParams(autoSearchParams);
      fetchEmployees({ ...autoSearchParams, page: 1, limit: pagination.pageSize });
      
      // 清空URL参数，避免重复搜索
      window.history.replaceState({}, document.title, '/employees');
    } else {
      // 正常初始化
      fetchEmployees();
    }
    
    fetchDepartments();
    fetchPositions();
    fetchStats();
  }, []);

  // 处理分页变化（未直接使用，分页在子组件中触发）
  const handleTableChange = (pagination) => {
    setPagination(pagination);
    fetchEmployees({ page: pagination.current, limit: pagination.pageSize });
  };

  // 处理搜索
  const handleSearch = (values) => {
    // 处理时间范围参数
    const processedValues = { ...values };
    if (values.dateRange && values.dateRange.length === 2) {
      const [startDate, endDate] = values.dateRange;
      processedValues.start_date = startDate.format('YYYY-MM-DD');
      processedValues.end_date = endDate.format('YYYY-MM-DD');
      // 移除原始的dateRange，避免传递给后端
      delete processedValues.dateRange;
    }
    
    setSearchParams(values); // 保存原始值用于表单显示
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchEmployees({ ...processedValues, page: 1, limit: pagination.pageSize });
  };

  // 重置搜索
  const handleReset = () => {
    setSearchParams({});
    setPagination(prev => ({ ...prev, current: 1 }));
    // 传递明确的空搜索参数，确保重置时清空所有搜索条件
    fetchEmployees({ 
      page: 1, 
      limit: pagination.pageSize,
      search: undefined,
      department_id: undefined,
      position_id: undefined,
      employment_status: undefined,
      progress: undefined,
      start_date: undefined,
      end_date: undefined
    });
  };



  // 查看员工详情
  const showDetail = (employee) => {
    setViewingEmployee(employee);
    setDetailVisible(true);
  };

  // 关闭详情抽屉
  const handleDetailClose = () => {
    setDetailVisible(false);
    setViewingEmployee(null);
    // 刷新员工列表和统计数据
    fetchEmployees({ page: pagination.current, limit: pagination.pageSize, ...searchParams });
  };



  // 保存员工信息
  const handleSaveEmployee = async (employeeId, values) => {
    try {
      console.log('💾 开始保存员工信息:', { employeeId, values });
      const response = await api.put(`/employees/${employeeId}`, values);
      console.log('📡 后端返回结果:', response.data);
      
      if (response.data.success) {
        message.success('员工信息更新成功');
        
        // 刷新列表，保持当前的分页和搜索条件
        console.log('🔄 刷新员工列表...');
        await fetchEmployees({ page: pagination.current, limit: pagination.pageSize, ...searchParams });
        
        // 如果当前正在查看该员工，需要重新获取完整的员工信息（包括关联的部门名称、岗位名称等）
        if (viewingEmployee && viewingEmployee.id === employeeId) {
          try {
            console.log('🔄 重新获取员工详情信息...');
            const employeeResponse = await api.get(`/employees/${employeeId}`);
            console.log('📡 员工详情返回:', employeeResponse.data);
            if (employeeResponse.data.success) {
              setViewingEmployee(employeeResponse.data.data);
              console.log('✅ 员工详情已更新');
            }
          } catch (err) {
            console.error('❌ 重新获取员工信息失败:', err);
          }
        }
        
        return true;
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('❌ 保存员工信息失败:', error);
      message.error('更新失败：' + error.message);
      throw error;
    }
  };

  // 更新员工状态
  const handleStatusChange = async (employeeId, newStatus) => {
    try {
      const response = await api.put(`/employees/${employeeId}`, {
        status: newStatus
      });
      if (response.data.success) {
        message.success('状态更新成功');
        // 刷新列表，保持当前的分页和搜索条件
        fetchEmployees({ page: pagination.current, limit: pagination.pageSize, ...searchParams });
        
        // 如果当前正在查看该员工，需要重新获取完整的员工信息
        if (viewingEmployee && viewingEmployee.id === employeeId) {
          try {
            const employeeResponse = await api.get(`/employees/${employeeId}`);
            if (employeeResponse.data.success) {
              setViewingEmployee(employeeResponse.data.data);
            }
          } catch (err) {
            console.error('重新获取员工信息失败:', err);
          }
        }
        
        return true;
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      message.error('状态更新失败：' + error.message);
      throw error;
    }
  };


  return (
    <main className="ant-layout-content css-l9pxc0">
      <div style={{ padding: '10px 16px' }}>

      
      {/* 统计卡片 */}
      <Card style={{ marginBottom: 16 }}>
        <EmployeeStats stats={stats} />
      </Card>

      {/* 搜索表单 */}
      <div style={{ marginBottom: 16 }}>
        <EmployeeSearchForm
          departments={departments}
          positions={positions}
          onSearch={handleSearch}
          onReset={handleReset}
          searchParams={searchParams}
        />
      </div>

      {/* 员工列表 */}
      <Card 
        title="员工列表"
        extra={
          <Button
            type="default"
            icon={<ReloadOutlined />}
            onClick={() => fetchEmployees({ page: pagination.current, limit: pagination.pageSize, ...searchParams })}
            loading={loading}
          >
            刷新
          </Button>
        }
      >
        <EmployeeList
          employees={employees}
          loading={loading}
          pagination={pagination}
          onPageChange={(page, pageSize) => {
            setPagination(prev => ({ ...prev, current: page, pageSize }));
            fetchEmployees({ page, limit: pageSize });
          }}
          onView={showDetail}
          onRefresh={() => fetchEmployees(searchParams)}
          currentUser={user}
        />
      </Card>



      {/* 员工详情抽屉 */}
      <Drawer
        title={viewingEmployee ? `${viewingEmployee.name} - 员工详情` : "员工详情"}
        placement="right"
        width="70%"
        onClose={handleDetailClose}
        open={detailVisible}
      >
        {viewingEmployee && (
          <EmployeeDetail 
            employee={viewingEmployee}
            onSave={handleSaveEmployee}
            onStatusChange={handleStatusChange}
            departments={departments}
            positions={positions}
          />
        )}
      </Drawer>
    </div>
    </main>
  );
};

export default Employees; 